/**
 * Spec canónico de filtro de columna, compartido por todos los listados.
 *
 * Un filtro es siempre `{ value, operator, start, end }`. `operator` viaja por
 * el cable como alias (`gte`, `lt`, …) porque `<` y `>` crudos rompen los query
 * strings; dentro de la app siempre se usa la forma UI (`>=`, `<`, …).
 *
 * Espejo de `back/utils/columnFilterSpec.js` — mantener ambos en sintonía.
 */

/** Alias de cable por operador UI. */
export const WIRE_OPERATOR_BY_UI = {
  "=": "eq",
  "!=": "ne",
  ">": "gt",
  ">=": "gte",
  "<": "lt",
  "<=": "lte",
  between: "between",
  contains: "contains",
  regex: "regex",
  startswith: "startswith",
  endswith: "endswith",
  is_null: "is_null",
  is_not_null: "is_not_null",
};

/** Operador UI por alias de cable. */
export const UI_OPERATOR_BY_WIRE = Object.keys(WIRE_OPERATOR_BY_UI).reduce(
  (acc, uiOperator) => {
    acc[WIRE_OPERATOR_BY_UI[uiOperator]] = uiOperator;
    return acc;
  },
  {}
);

/** Operadores que no llevan valor: el propio operador es el filtro. */
export const VALUELESS_OPERATORS = ["is_null", "is_not_null"];

/** Separador del rango de fechas en el protocolo plano heredado. */
export const LEGACY_DATE_RANGE_SEPARATOR = " - ";

/** Operadores del protocolo plano heredado que van como `prefijo:valor`. */
const LEGACY_PREFIXED_OPERATORS = [
  "contains",
  "startswith",
  "endswith",
  "regex",
];

/**
 * Normaliza a operador UI. Acepta alias de cable y forma UI indistintamente.
 * @param {string|null|undefined} operator
 * @returns {string}
 */
export function normalizeOperator(operator) {
  if (operator == null || operator === "") return "=";
  const raw = String(operator);
  if (Object.prototype.hasOwnProperty.call(UI_OPERATOR_BY_WIRE, raw)) {
    return UI_OPERATOR_BY_WIRE[raw];
  }
  return raw;
}

/**
 * Convierte a alias de cable. Acepta forma UI y alias indistintamente.
 * @param {string|null|undefined} operator
 * @returns {string}
 */
export function toWireOperator(operator) {
  const ui = normalizeOperator(operator);
  return WIRE_OPERATOR_BY_UI[ui] || ui;
}

/** @param {string} operator */
export function isValuelessOperator(operator) {
  return VALUELESS_OPERATORS.includes(normalizeOperator(operator));
}

/** @returns {{ value: null, operator: string, start: string, end: string }} */
export function emptyFilterSpec() {
  return { value: null, operator: "=", start: "", end: "" };
}

/**
 * Extrae la parte `YYYY-MM-DD` de una fecha en cualquier formato razonable.
 * Devuelve "" si no es una fecha usable.
 * @param {string|Date|null|undefined} value
 * @returns {string}
 */
export function datePartYYYYMMDD(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Lleva cualquier spec parcial a la forma canónica con operador UI.
 * @param {object|null|undefined} raw
 */
export function normalizeFilterSpec(raw) {
  if (!raw || typeof raw !== "object") return emptyFilterSpec();
  return {
    value: raw.value === undefined ? null : raw.value,
    operator: normalizeOperator(raw.operator),
    start: raw.start || "",
    end: raw.end || "",
  };
}

/**
 * ¿El filtro está activo? Única definición del sistema: la usan el ícono de la
 * cabecera, los chips y el filtrado real en el backend.
 *
 * Un array vacío NO está activo (un multiselect vaciado no filtra nada).
 * @param {object|null|undefined} spec
 * @returns {boolean}
 */
export function isFilterSpecActive(spec) {
  if (!spec || typeof spec !== "object") return false;
  const operator = normalizeOperator(spec.operator);
  if (isValuelessOperator(operator)) return true;
  if (operator === "between") {
    return Boolean(
      datePartYYYYMMDD(spec.start || "") && datePartYYYYMMDD(spec.end || "")
    );
  }
  const value = spec.value;
  if (value !== null && value !== undefined && value !== "") {
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  }
  return Boolean(spec.start || spec.end);
}

/**
 * Parsea el parámetro `columnFilters` (JSON) a specs canónicos por columna.
 * @param {object|string|null|undefined} raw
 * @returns {object|null} mapa `{ [columnKey]: spec }`, o null si no hay nada
 */
export function parseColumnFiltersQuery(raw) {
  if (raw == null || raw === "") return null;
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch (_e) {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return null;
  }
  const normalized = {};
  Object.keys(parsed).forEach((key) => {
    if (!parsed[key] || typeof parsed[key] !== "object") return;
    normalized[key] = normalizeFilterSpec(parsed[key]);
  });
  return normalized;
}

/**
 * Serializa los filtros activos al formato de cable (operadores como alias).
 * @param {object|null|undefined} specsByKey mapa `{ [columnKey]: spec }`
 * @returns {object|null} null si no hay ninguno activo
 */
export function serializeActiveColumnFilters(specsByKey) {
  const out = {};
  Object.keys(specsByKey || {}).forEach((key) => {
    const spec = specsByKey[key];
    if (!isFilterSpecActive(spec)) return;
    out[key] = {
      value: spec.value === undefined ? null : spec.value,
      operator: toWireOperator(spec.operator),
      start: spec.start || "",
      end: spec.end || "",
    };
  });
  return Object.keys(out).length ? out : null;
}

/**
 * Quita el prefijo "between" que los guardados viejos concatenaban al rango.
 * Un rango ya se identifica por el separador, así que el prefijo nunca hizo
 * falta: solo ensuciaba la fecha de inicio, y se re-concatenaba en cada
 * guardado del reporte (`betweenbetween2026-09-01 - 2026-09-30`), dejando el
 * filtro inválido —sin pastilla en la UI y sin filtrar nada en el backend—.
 * @param {any} value
 * @returns {any} el mismo valor si no es texto
 */
export function stripLegacyBetweenPrefix(value) {
  if (typeof value !== "string") return value;
  return value.replace(/^(?:between)+/i, "");
}

/**
 * Decodifica el rango de fechas del protocolo plano heredado, donde un
 * "between" viaja como inicio + separador + fin dentro de un solo valor.
 * @param {string|null|undefined} rawValue
 * @returns {{ start: string, end: string }}
 */
export function legacyDateRangeFromValue(rawValue) {
  if (!rawValue || typeof rawValue !== "string") {
    return { start: "", end: "" };
  }
  const value = stripLegacyBetweenPrefix(rawValue);
  if (value.includes(LEGACY_DATE_RANGE_SEPARATOR)) {
    const [start, end] = value
      .split(LEGACY_DATE_RANGE_SEPARATOR)
      .map((part) => part.trim());
    return { start: start || "", end: end || "" };
  }
  return { start: value, end: "" };
}

/**
 * Construye un spec canónico desde el estado de `Filters.vue`, que guarda cada
 * filtro repartido en tres mapas paralelos (`filters`, `filterOperators`,
 * `tempDateRanges`).
 * @param {{ value?: any, operator?: string, dateRange?: { start?: string, end?: string }, isDateField?: boolean }} state
 */
export function specFromLegacyState(state) {
  const source = state || {};
  const range = source.dateRange || {};
  let start = range.start || "";
  let end = range.end || "";
  // Los rangos aún no hidratados viven dentro del propio valor plano.
  if (!start && !end && source.isDateField) {
    const decoded = legacyDateRangeFromValue(source.value);
    start = decoded.start;
    end = decoded.end;
  }
  return normalizeFilterSpec({
    value: source.value,
    operator: source.operator,
    start,
    end,
  });
}

/**
 * Convierte un spec al valor plano del protocolo heredado, tal como lo espera
 * el query-builder de los modelos dinámicos (`contains:x`, `>=5`, `is_null`,
 * `"2026-01-01 - 2026-02-01"`).
 * @param {object} rawSpec
 * @returns {string|number|boolean|null}
 */
export function legacyValueFromSpec(rawSpec) {
  const spec = normalizeFilterSpec(rawSpec);
  const operator = spec.operator;
  if (isValuelessOperator(operator)) return operator;

  const start = spec.start || "";
  const end = spec.end || "";
  if (operator === "between") {
    // El rango puede venir en `start`/`end` o todavía plano dentro del valor.
    let from = start;
    let to = end;
    if (!from || !to) {
      const decoded = legacyDateRangeFromValue(spec.value);
      from = decoded.start;
      to = decoded.end;
    }
    if (!from || !to) return null;
    return `${from}${LEGACY_DATE_RANGE_SEPARATOR}${to}`;
  }

  const value =
    spec.value !== null && spec.value !== undefined && spec.value !== ""
      ? spec.value
      : start || end || null;
  if (value === null) return null;

  if (Array.isArray(value)) {
    const ids = value
      .filter((item) => item !== null && item !== undefined)
      .map((item) =>
        typeof item === "object" && item._id != null ? item._id : item
      );
    return ids.length ? ids.join(",") : null;
  }

  const scalar =
    typeof value === "object" && value._id != null ? value._id : value;
  if (LEGACY_PREFIXED_OPERATORS.includes(operator)) {
    return `${operator}:${scalar}`;
  }
  if (operator === "=") return scalar;
  return `${operator}${scalar}`;
}
